/**
 * Persistent floating Message workspace state.
 * Survives StaffChatPopup unmount and other popups opening.
 * Isolated from popupUIStore — does not change global popup behavior.
 */

const listeners = new Set();

/** Persists last-read message id per conversation across refresh. */
const STAFF_LAST_READ_KEY = "staff_chat_last_read_by_conversation";
/** Persists read-but-unreplied tracking for „Răspunzi?” nudge. */
const STAFF_AWAITING_REPLY_KEY = "staff_chat_awaiting_reply";

/** Grace period after reading a student message before showing „Răspunzi?” */
export const AWAITING_REPLY_GRACE_MS = 45 * 60 * 1000;
export const AWAITING_REPLY_SNOOZE_HOURS = 4;

const DEFAULT_STATE = {
   isOpen: false,
   dockMode: "expanded", // "expanded" | "retracted"
   activeTab: "conversations", // "conversations" | "contacts"
   view: "list", // "list" | "thread"
   selectedConversationId: null,
   selectedStudent: null,
   /** Prefill for composer (e.g. reply-to-review). Token forces apply even if text repeats. */
   composerDraft: "",
   composerDraftToken: 0,
   /** @type {Record<string, number>} conversationId -> unread message count */
   unreadByConversationId: {},
   /** Bumped when awaiting-reply localStorage map changes (for subscribers). */
   awaitingReplyVersion: 0,
   openProps: null,
};

let state = { ...DEFAULT_STATE };

function readStaffLastReadMap() {
   if (typeof window === "undefined") return {};
   try {
      const raw = window.localStorage.getItem(STAFF_LAST_READ_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
   } catch {
      return {};
   }
}

function writeStaffLastReadMap(map) {
   if (typeof window === "undefined") return;
   try {
      window.localStorage.setItem(STAFF_LAST_READ_KEY, JSON.stringify(map));
   } catch {
      // ignore quota / private mode
   }
}

export function getStaffChatLastReadMessageId(conversationId) {
   if (conversationId == null) return null;
   const map = readStaffLastReadMap();
   const raw = map[String(conversationId)];
   return raw != null && raw !== "" ? String(raw) : null;
}

/** Remember the newest message id the staff user has seen in this conversation. */
export function setStaffChatLastReadMessageId(conversationId, messageId) {
   if (conversationId == null || messageId == null) return;
   const key = String(conversationId);
   const nextId = String(messageId);
   const map = readStaffLastReadMap();
   const prev = map[key];
   if (prev != null && Number(prev) >= Number(nextId)) return;
   map[key] = nextId;
   writeStaffLastReadMap(map);
}

function readAwaitingReplyMap() {
   if (typeof window === "undefined") return {};
   try {
      const raw = window.localStorage.getItem(STAFF_AWAITING_REPLY_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
   } catch {
      return {};
   }
}

function writeAwaitingReplyMap(map) {
   if (typeof window === "undefined") return;
   try {
      window.localStorage.setItem(STAFF_AWAITING_REPLY_KEY, JSON.stringify(map));
   } catch {
      // ignore quota / private mode
   }
}

function bumpAwaitingReplyVersion() {
   setState({ awaitingReplyVersion: (state.awaitingReplyVersion || 0) + 1 });
}

function isStudentLastMessage(message) {
   const role = String(message?.senderRole || "").toUpperCase();
   return role === "STUDENT" || role === "USER";
}

/**
 * After staff reads a thread whose last message is from the student,
 * start (or keep) the awaiting-reply timer. Same messageId keeps original readAt.
 */
export function trackStaffChatReadForReply(conversationId, lastMessage) {
   if (conversationId == null) return;
   if (!isStudentLastMessage(lastMessage) || lastMessage?.id == null) {
      clearStaffChatAwaitingReply(conversationId);
      return;
   }

   const key = String(conversationId);
   const messageId = String(lastMessage.id);
   const map = readAwaitingReplyMap();
   const prev = map[key];
   if (prev && String(prev.messageId) === messageId) {
      // Same pending student message — do not reset the grace clock.
      return;
   }

   map[key] = {
      messageId,
      readAt: Date.now(),
   };
   writeAwaitingReplyMap(map);
   bumpAwaitingReplyVersion();
}

export function clearStaffChatAwaitingReply(conversationId) {
   if (conversationId == null) return;
   const key = String(conversationId);
   const map = readAwaitingReplyMap();
   if (!map[key]) return;
   delete map[key];
   writeAwaitingReplyMap(map);
   bumpAwaitingReplyVersion();
}

export function snoozeStaffChatAwaitingReply(
   conversationId,
   hours = AWAITING_REPLY_SNOOZE_HOURS,
) {
   if (conversationId == null) return;
   const key = String(conversationId);
   const map = readAwaitingReplyMap();
   const prev = map[key];
   if (!prev?.messageId) return;
   const ms =
      Math.max(0.25, Number(hours) || AWAITING_REPLY_SNOOZE_HOURS) *
      60 *
      60 *
      1000;
   map[key] = {
      ...prev,
      snoozedUntil: Date.now() + ms,
   };
   writeAwaitingReplyMap(map);
   bumpAwaitingReplyVersion();
}

export function dismissStaffChatAwaitingReply(conversationId, messageId) {
   if (conversationId == null || messageId == null) return;
   const key = String(conversationId);
   const map = readAwaitingReplyMap();
   const prev = map[key];
   if (!prev) return;
   map[key] = {
      ...prev,
      dismissedForMessageId: String(messageId),
      snoozedUntil: undefined,
   };
   writeAwaitingReplyMap(map);
   bumpAwaitingReplyVersion();
}

/**
 * Whether to show „Răspunzi?” for this conversation.
 * Requires: last msg from student, already read (not unread), past grace, not snoozed/dismissed.
 */
export function getStaffChatAwaitingReplyState(
   conversationId,
   { lastMessage = null, unreadCount = 0, now = Date.now() } = {},
) {
   if (conversationId == null) return { show: false, ageMs: 0 };
   if (Number(unreadCount) > 0) return { show: false, ageMs: 0 };
   if (!isStudentLastMessage(lastMessage) || lastMessage?.id == null) {
      return { show: false, ageMs: 0 };
   }

   const lastRead = getStaffChatLastReadMessageId(conversationId);
   if (!lastRead || Number(lastMessage.id) > Number(lastRead)) {
      return { show: false, ageMs: 0 };
   }

   const entry = readAwaitingReplyMap()[String(conversationId)];
   if (!entry || String(entry.messageId) !== String(lastMessage.id)) {
      return { show: false, ageMs: 0 };
   }

   if (
      entry.dismissedForMessageId != null &&
      String(entry.dismissedForMessageId) === String(lastMessage.id)
   ) {
      return { show: false, ageMs: 0 };
   }

   if (entry.snoozedUntil != null && Number(entry.snoozedUntil) > now) {
      return { show: false, ageMs: 0 };
   }

   const ageMs = Math.max(0, now - Number(entry.readAt || 0));
   if (ageMs < AWAITING_REPLY_GRACE_MS) {
      return { show: false, ageMs };
   }

   return { show: true, ageMs };
}

export function countStaffChatAwaitingReply(conversations, now = Date.now()) {
   let count = 0;
   for (const item of conversations || []) {
      if (item?.id == null) continue;
      const unread = getStaffChatUnreadCount(item.id);
      const stateReply = getStaffChatAwaitingReplyState(item.id, {
         lastMessage: item.lastMessage,
         unreadCount: unread,
         now,
      });
      if (stateReply.show) count += 1;
   }
   return count;
}

function emit() {
   listeners.forEach((cb) => {
      try {
         cb(state);
      } catch {
         // ignore subscriber errors
      }
   });
}

function setState(partial) {
   state = { ...state, ...partial };
   emit();
}

export function getStaffChatWorkspaceState() {
   return state;
}

export function subscribeStaffChatWorkspace(cb) {
   listeners.add(cb);
   return () => listeners.delete(cb);
}

export function openStaffChatWorkspace(props = {}) {
   const next = {
      isOpen: true,
      dockMode: "expanded",
      openProps: props && typeof props === "object" ? props : null,
   };

   if (props?.openThread && (props?.conversationId != null || props?.student)) {
      next.view = "thread";
      if (props.conversationId != null) {
         next.selectedConversationId = props.conversationId;
      } else if (props?.student) {
         // Opening by student only — don't keep a previous conversation id.
         next.selectedConversationId = null;
      }
      if (props.student) {
         next.selectedStudent = props.student;
      }
   }

   if (props?.composerDraft != null) {
      next.composerDraft = String(props.composerDraft);
      next.composerDraftToken = Date.now();
   }

   setState(next);
}

export function clearStaffChatComposerDraft() {
   if (!state.composerDraft && !state.composerDraftToken) return;
   setState({ composerDraft: "", composerDraftToken: 0 });
}

export function buildReviewReplyDraft(rating) {
   const stars = Math.round(Number(rating?.rating));
   const starsSafe = Number.isFinite(stars) && stars > 0 ? stars : 0;
   const starsLabel =
      starsSafe === 1 ? "1 stea" : `${starsSafe || "—"} stele`;
   const comment = String(rating?.comment || "").trim();
   const reviewBlock = comment ? `„${comment}”` : "(fără comentariu)";

   return (
      `Salut! Am văzut recenzia ta (${starsLabel}):\n` +
      `${reviewBlock}\n\n` +
      `Poți să-mi spui motivul pentru care ai lăsat această recenzie?\n`
   );
}

function positiveIdOrNull(value) {
   const id = Number(value);
   return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * Elevul unei recenzii. Randurile de la `/instructor-ratings/admin/all` nu au
 * intotdeauna obiectul `user` populat, dar au id-ul plat (`userId`) - folosit
 * deja pentru cheia de lista. Fara acest fallback, „Raspunde" eseua pentru
 * fiecare rand fara `user` embedat, la manageri si la admini la fel.
 * Numele lipsa se completeaza din lista de elevi a chatului.
 */
function resolveReviewStudent(rating) {
   const embedded = rating?.user ?? rating?.student ?? null;
   const embeddedId = positiveIdOrNull(embedded?.id);
   if (embeddedId != null) return embedded;

   const flatId = positiveIdOrNull(
      rating?.userId ?? rating?.user_id ?? rating?.studentId ?? rating?.student_id,
   );
   if (flatId == null) return null;

   return embedded && typeof embedded === "object"
      ? { ...embedded, id: flatId }
      : { id: flatId };
}

/**
 * Id-ul elevului unei recenzii, sau null. Exportat ca „Raspunde" si verificarea
 * de scope pe grupe sa foloseasca aceeasi definitie.
 */
export function readReviewStudentId(rating) {
   const student = resolveReviewStudent(rating);
   return student ? positiveIdOrNull(student.id) : null;
}

/** Opens message workspace on the student's thread with review text prefilled. */
export function openStaffChatReplyToReview(rating) {
   const student = resolveReviewStudent(rating);
   if (!student) return false;

   openStaffChatWorkspace({
      openThread: true,
      student,
      composerDraft: buildReviewReplyDraft(rating),
   });
   return true;
}

export function closeStaffChatWorkspace() {
   setState({
      isOpen: false,
      dockMode: "expanded",
      openProps: null,
      // keep view / selection / tab / unread — conversation persists
   });
}

export function expandStaffChatWorkspace() {
   if (!state.isOpen) return;
   if (state.dockMode === "expanded") return;
   setState({ dockMode: "expanded" });
}

export function retractStaffChatWorkspace() {
   if (!state.isOpen) return;
   if (state.dockMode === "retracted") return;
   setState({ dockMode: "retracted" });
}

export function setStaffChatActiveTab(tab) {
   const next =
      tab === "contacts" || tab === "groups" ? tab : "conversations";
   if (state.activeTab === next) return;
   setState({ activeTab: next });
}

export function setStaffChatView(view) {
   const next = view === "thread" ? "thread" : "list";
   if (state.view === next) return;
   setState({ view: next });
}

export function setStaffChatSelection({
   conversationId = undefined,
   student = undefined,
   view = undefined,
} = {}) {
   const patch = {};
   if (conversationId !== undefined) {
      patch.selectedConversationId = conversationId;
   }
   if (student !== undefined) {
      patch.selectedStudent = student;
   }
   if (view !== undefined) {
      patch.view = view === "thread" ? "thread" : "list";
   }
   setState(patch);
}

export function clearStaffChatThreadSelection() {
   setState({
      view: "list",
      selectedConversationId: null,
      selectedStudent: null,
      // Drop one-shot open props (e.g. reply-from-review) so list chrome/padding restore.
      openProps: null,
   });
}

export function getStaffChatUnreadTotal() {
   return Object.values(state.unreadByConversationId).reduce(
      (sum, n) => sum + (Number(n) > 0 ? Number(n) : 0),
      0,
   );
}

export function getStaffChatUnreadCount(conversationId) {
   if (conversationId == null) return 0;
   return Number(state.unreadByConversationId[String(conversationId)] || 0);
}

/**
 * Clear in-memory unread and optionally persist the last seen message id
 * so refresh does not resurrect the badge.
 */
export function markStaffChatConversationRead(conversationId, lastMessageId) {
   if (conversationId == null) return;
   const key = String(conversationId);
   if (lastMessageId != null) {
      setStaffChatLastReadMessageId(conversationId, lastMessageId);
   }
   if (!state.unreadByConversationId[key]) return;
   const next = { ...state.unreadByConversationId };
   delete next[key];
   setState({ unreadByConversationId: next });
}

export function incrementStaffChatUnread(conversationId, by = 1) {
   if (conversationId == null) return;
   const key = String(conversationId);
   const active =
      state.isOpen &&
      state.view === "thread" &&
      state.dockMode === "expanded" &&
      Number(state.selectedConversationId) === Number(conversationId);
   if (active) return;

   const prev = Number(state.unreadByConversationId[key] || 0);
   setState({
      unreadByConversationId: {
         ...state.unreadByConversationId,
         [key]: prev + Math.max(1, Number(by) || 1),
      },
   });
}

/**
 * Seed unread from conversation list:
 * last message from student AND newer than last-read id ⇒ unread.
 * Already-read threads stay clear across refresh.
 */
export function syncStaffChatUnreadFromConversations(conversations, activeId) {
   const next = { ...state.unreadByConversationId };
   for (const item of conversations || []) {
      if (item?.id == null) continue;
      const key = String(item.id);
      const lastMsg = item?.lastMessage;
      const lastMsgId = lastMsg?.id;

      if (activeId != null && Number(item.id) === Number(activeId)) {
         delete next[key];
         if (lastMsgId != null) {
            setStaffChatLastReadMessageId(item.id, lastMsgId);
         }
         continue;
      }

      const role = String(lastMsg?.senderRole || "").toUpperCase();
      const fromStudent = role === "STUDENT" || role === "USER";
      if (!fromStudent || lastMsgId == null) {
         // No pending student message — drop stale local unread for this thread.
         if (next[key] && !fromStudent) delete next[key];
         if (!fromStudent) clearStaffChatAwaitingReply(item.id);
         continue;
      }

      const lastRead = getStaffChatLastReadMessageId(item.id);
      if (lastRead != null && Number(lastMsgId) <= Number(lastRead)) {
         delete next[key];
         continue;
      }

      next[key] = Math.max(1, Number(next[key] || 0));
   }
   setState({ unreadByConversationId: next });
}
